import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useOutletContext } from 'react-router';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import type { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { ListWithDetailLayout } from '@sorare/core/src/components/layout/ListWithDetailLayout';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE,
  ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { EventTypeSwitcher } from 'components/fixture/EventTypeSwitcher';
import { FixtureSelector } from 'components/fixture/FixtureSelector';
import { UserSearchBar } from 'components/user/UserSearchBar';
import { getFixtureQueryVariables } from 'lib/so5';
import { UserFixtureResultLink } from 'my-circles/UserFixtureResultLink';

import type {
  MyCircleLayoutQuery,
  MyCircleLayoutQueryVariables,
} from './__generated__/layout.graphql';
import type { LayoutParams } from './__generated__/routeParams';

const Root = styled.div`
  padding: var(--double-unit);
`;
const EventTypeSwitcherWrapper = styled(Vertical)`
  margin-bottom: var(--unit);

  @media ${laptopAndAbove} {
    margin-bottom: 0;
    align-self: center;
  }
`;

const MY_CIRCLE_LAYOUT_QUERY = gql`
  query MyCircleLayoutQuery(
    $slug: String
    $type: So5State
    $after: String
    $sport: Sport!
    $eventType: So5FixtureEvent!
    $userSlug: String!
  ) {
    so5 {
      so5Fixture(
        slug: $slug
        sport: $sport
        type: $type
        eventType: $eventType
      ) {
        slug
        type
        ...FixtureSelector_so5Fixture
        ...UserFixtureResultLink_so5Fixture
        userFixtureResults(userSlug: $userSlug) {
          slug
          user {
            slug
            followed {
              slug
            }
          }
          ...UserFixtureResultLink_userFixtureResults
        }
        myFriendsFixtureResults(first: 8, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          nodes {
            slug
            ...UserFixtureResultLink_userFixtureResults
          }
        }
      }
    }
  }
  ${FixtureSelector.fragments.so5Fixture}
  ${UserFixtureResultLink.fragments.userFixtureResults}
  ${UserFixtureResultLink.fragments.so5Fixture}
` as TypedDocumentNode<MyCircleLayoutQuery, MyCircleLayoutQueryVariables>;

export const loader = queryLoader<LayoutParams>(
  MY_CIRCLE_LAYOUT_QUERY,
  ({ fixture, sport, eventType, slug }) => ({
    sport,
    ...getFixtureQueryVariables(fixture),
    eventType: eventType?.toUpperCase() as So5FixtureEvent,
    // Dirty default fallback but needs a working value to not trigger NOT_FOUND error
    userSlug: slug || 'sorare',
  }),
  // avoid black screen when navigating to a new fixture
  { preventTransitionBeforeLoaded: true }
);

type LayoutData = {
  firstUserSlug?: string;
  refetchList: () => void;
};

export const useLayoutData = () => {
  return useOutletContext<LayoutData>();
};

const Circles = () => {
  const track = useEvents();
  const { generateSportContextPath } = useSportContext();
  const { data, loading, loadMore, refetch } =
    useQueryLoader<MyCircleLayoutQuery>();
  const { so5Fixture } = data.so5 || {};
  const isLaptopAndAbove = useIsLaptop();
  const { myFriendsFixtureResults } = so5Fixture || {};
  const { nodes = [] } = myFriendsFixtureResults || {};
  const { slug, fixture, eventType } = useTypedParams<LayoutParams>();
  const [isNavigating, setIsNavigating] = useState('');
  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(
        false,
        {
          after: myFriendsFixtureResults?.pageInfo?.endCursor,
        },
        'UserFixtureResultsConnection'
      );
    },
    !!myFriendsFixtureResults?.pageInfo?.hasNextPage,
    loading
  );

  if (fixture !== so5Fixture?.slug) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE, {
          params: {
            eventType,
            fixture: so5Fixture?.slug || '',
          },
        })}
        replace
      />
    );
  }

  if (
    isNavigating &&
    so5Fixture?.displayName &&
    isNavigating === so5Fixture?.displayName
  ) {
    setIsNavigating('');
  }

  const userFixtureResults = nodes || [];

  const selectedUserFixtureResults = so5Fixture?.userFixtureResults;

  const selectedUserFixtureResultInCircle =
    selectedUserFixtureResults &&
    userFixtureResults
      .map(({ slug: _slug }) => _slug)
      .includes(selectedUserFixtureResults.slug);

  // Need to check the slug existence to not display dirty sorare fallback
  const displaySelectedUserFixtureResult =
    slug && !selectedUserFixtureResultInCircle;

  const displaySearchedSection = !selectedUserFixtureResults?.user.followed;

  const eventTypeSwitcher = (
    <EventTypeSwitcherWrapper>
      <EventTypeSwitcher
        getTo={type =>
          generateSportContextPath(ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE, {
            params: { eventType: type, fixture: 'liveOrPast' },
          })
        }
      />
    </EventTypeSwitcherWrapper>
  );

  return (
    <ListWithDetailLayout
      noPadding
      noRightPanelPadding
      outletContext={{
        firstUserSlug: nodes.at(0)?.user.slug,
        refetchList: refetch,
      }}
      forceNoMobileDialog={!slug}
    >
      <PageHeader text={glossary.myCircle} />
      <Root>
        <Vertical gap={2}>
          {isLaptopAndAbove ? (
            eventTypeSwitcher
          ) : (
            <Portal id="above-bottom-bar-portal">{eventTypeSwitcher}</Portal>
          )}
          <FixtureSelector
            currentFixture={so5Fixture}
            withMyCircleFooter
            disableUpcoming
            pickerSize="big"
            navigateTo={fixtureSlug => {
              if (slug) {
                return generateSportContextPath(
                  ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE_SLUG,
                  {
                    params: {
                      slug,
                      fixture: fixtureSlug,
                      eventType: so5Fixture?.type || '',
                    },
                  }
                );
              }

              return generateSportContextPath(
                ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE,
                {
                  params: {
                    fixture: fixtureSlug,
                    eventType: so5Fixture?.type || '',
                  },
                }
              );
            }}
            onFixtureChanged={gameweek => {
              track('Click My Circles Date Picker', {});
              setIsNavigating(gameweek);
            }}
          />
          <UserSearchBar
            to={userSlug =>
              generateSportContextPath(
                ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE_SLUG,
                {
                  params: {
                    slug: userSlug,
                    fixture,
                    eventType,
                  },
                }
              )
            }
          />
          {isNavigating ? (
            <LoadingIndicator />
          ) : (
            !!so5Fixture && (
              <>
                {!!displaySelectedUserFixtureResult &&
                  !!selectedUserFixtureResults && (
                    <>
                      {!!displaySearchedSection && (
                        <BrandXS color="var(--c-nd-600)">
                          <FormattedMessage
                            id="my-circles.searched"
                            defaultMessage="Searched"
                          />
                        </BrandXS>
                      )}
                      <UserFixtureResultLink
                        userFixtureResults={selectedUserFixtureResults}
                        so5Fixture={so5Fixture}
                      />
                    </>
                  )}
                {!!displaySelectedUserFixtureResult &&
                  !!displaySearchedSection && (
                    <BrandXS color="var(--c-nd-600)">
                      <FormattedMessage {...glossary.myCircle} />
                    </BrandXS>
                  )}
                {userFixtureResults.map(userFixtureResult => (
                  <UserFixtureResultLink
                    key={userFixtureResult.slug}
                    userFixtureResults={userFixtureResult}
                    so5Fixture={so5Fixture}
                  />
                ))}
              </>
            )
          )}
          {!isNavigating && <InfiniteScrollLoader />}
        </Vertical>
      </Root>
    </ListWithDetailLayout>
  );
};
export default Circles;
